import { createContext, useContext, useReducer } from 'react';

const AppContext = createContext();

const initialState = {
  caseId: null,
  caseData: null,
  documents: [],
  pricing: null,
  folderTree: null,
  // caseDashboard holds the full /cases/{id}/dashboard envelope from
  // CaseDetail mount. Sub-tabs (InventoryTab, ChecklistTab, etc.) can read
  // their initial data here and skip a redundant on-mount fetch; their
  // polling loops continue to refresh independently. See Pool Governance P2.
  caseDashboard: null,
  processingActive: false,
  chatScope: null,
  // Cross-tab handoff for the roster's "Refine with agent" entry: the
  // Witnesses tab stashes { message } here, navigates to Ask Arc, and the
  // (always-mounted) ExplorationTab auto-sends it once. Cleared after pickup.
  pendingRefine: null,
  chatConversationId: null,
  chatHistory: [],
  chatLastEventSeq: 0,
  chatCostUsd: 0,
};

function appReducer(state, action) {
  switch (action.type) {
    case 'SET_CASE':
      return { ...initialState, caseId: action.id, caseData: action.data };
    case 'UPDATE_CASE_DATA':
      return { ...state, caseData: { ...state.caseData, ...action.data } };
    case 'SET_DOCUMENTS': {
      const docs = Array.isArray(action.docs) ? action.docs : (action.docs?.documents || []);
      return { ...state, documents: docs };
    }
    case 'MERGE_DOCUMENTS': {
      // Apply a delta payload from the inventory poll (?since=…) — only
      // documents whose own row OR whose related processing tasks changed
      // since the last poll. Existing docs are updated in place; new docs
      // prepend (newest-first matches the backend's created_at DESC order).
      // Pool Governance follow-on: keeps the inventory poll under a few KB
      // instead of re-downloading the full ~3.74MB list every 8s.
      const delta = Array.isArray(action.docs) ? action.docs : (action.docs?.documents || []);
      if (delta.length === 0) return state;
      const byId = new Map(delta.map(d => [d.id, d]));
      const merged = state.documents.map(d => {
        const incoming = byId.get(d.id);
        if (incoming) {
          byId.delete(d.id);
          return { ...d, ...incoming };
        }
        return d;
      });
      const newDocs = Array.from(byId.values());
      return { ...state, documents: [...newDocs, ...merged] };
    }
    case 'REMOVE_DOCUMENT':
      return { ...state, documents: state.documents.filter(d => d.id !== action.id) };
    case 'PATCH_DOCUMENT': {
      // Merge `action.fields` into the doc with id === action.id.
      // No-op if the doc isn't in the list (e.g. paginated off-page).
      const fields = action.fields || {};
      return {
        ...state,
        documents: state.documents.map(d =>
          d.id === action.id ? { ...d, ...fields } : d
        ),
      };
    }
    case 'SET_PRICING':
      return { ...state, pricing: action.pricing };
    case 'SET_FOLDER_TREE':
      return { ...state, folderTree: action.tree };
    case 'SET_CASE_DASHBOARD':
      return { ...state, caseDashboard: action.data };
    case 'SET_PROCESSING':
      return { ...state, processingActive: action.active };
    case 'SET_CHAT_SCOPE':
      return { ...state, chatScope: action.scope };
    case 'CLEAR_CHAT_SCOPE':
      return { ...state, chatScope: null };
    case 'SET_PENDING_REFINE':
      return { ...state, pendingRefine: action.payload };
    case 'CLEAR_PENDING_REFINE':
      return { ...state, pendingRefine: null };
    case 'SET_CONVERSATION':
      return {
        ...state,
        chatConversationId: action.id,
        chatHistory: action.history || [],
        chatLastEventSeq: action.seq || 0,
      };
    case 'APPEND_CHAT': {
      // Idempotent per turn: a finalized assistant message carries the
      // operation_id of its run. Multiple finalizers (done event, terminal
      // status fallback) may each dispatch for the same turn — converge to
      // one message instead of stacking duplicates. A partial fallback
      // never overwrites the full done-event message.
      const opId = action.msg.operation_id;
      if (opId) {
        const idx = state.chatHistory.findIndex(
          m => m.role === 'assistant' && m.operation_id === opId
        );
        if (idx !== -1) {
          if (!state.chatHistory[idx].partial && action.msg.partial) return state;
          const next = [...state.chatHistory];
          next[idx] = action.msg;
          return { ...state, chatHistory: next };
        }
      }
      return { ...state, chatHistory: [...state.chatHistory, action.msg] };
    }
    case 'UPDATE_CHAT_SEQ':
      return { ...state, chatLastEventSeq: action.seq };
    case 'ADD_CHAT_COST':
      return { ...state, chatCostUsd: state.chatCostUsd + action.cost };
    default:
      return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  return (
    <AppContext.Provider value={{ ...state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
